/**
 * CMG calculation helpers shared by the GED simulator (and reusable for any
 * future care type that reuses GED effort rates / overtime formulas).
 *
 * The GED simulator injects its per-child-age social-contribution ceiling
 * (GED_SOCIAL_CONTRIBUTION_MAX_BY_CHILD_AGE from cmgCalculationsGed.js) into
 * computeSocialContributionsCredit. Keeping the formulas here is the single
 * source of truth for GED-like CMG maths.
 */

export const CMG_EFFORT_RATE_HOURLY = {
  1: 0.1238,
  2: 0.1032,
  3: 0.0826,
  4: 0.062,
  5: 0.062,
  6: 0.062,
  7: 0.062,
  8: 0.0412,
  9: 0.0412,
}

export const CMG_HOURLY_WAGE_REFERENCE = 10.5

export function computeGrossForSimpleAndSharedHours(simpleHours, sharedHours, rate, sharedCostRatio) {
  return simpleHours * rate + sharedHours * rate * sharedCostRatio
}

export function computeEmployerContributionsReductionCost(region) {
  if (region === 'la_reunion') return 4.2
  if (['guadeloupe', 'guyana', 'martinique'].includes(region)) return 3.7
  return 2
}

export function computeEmployerContributionsReduction({
  regularHours,
  monthlyOvertimeFloat,
  employerContributionsReductionCost,
  socialContributionsEmployer,
  socialContributionsEmployee,
}) {
  const hoursConsidered = Math.ceil(regularHours * 0.9 + monthlyOvertimeFloat)
  const possibleEmployerReduction = hoursConsidered * employerContributionsReductionCost
  const maxPossibleReduction = Math.max(
    (socialContributionsEmployer + socialContributionsEmployee) / 2,
    0,
  )
  return Math.min(possibleEmployerReduction, maxPossibleReduction)
}

// `socialContributionMaxByChildAge` is the care-type-specific ceiling table
// (GED vs Person differ only on this): injected by the care-type wrapper.
export function computeSocialContributionsCredit({
  cmgUnavailable,
  socialContributionsEmployer,
  socialContributionsEmployee,
  childAge,
  socialContributionMaxByChildAge,
}) {
  if (cmgUnavailable) return 0
  const totalSocialContribution = socialContributionsEmployer + socialContributionsEmployee
  const maxAmount = socialContributionMaxByChildAge[parseInt(childAge, 10)] ?? 0
  return Math.min(totalSocialContribution / 2, maxAmount)
}

export function computeCreditTaxCeil({ children, firstEmployeeDeclaration }) {
  const increaseAmount = children * 1500
  const amountFirstYear = Math.min(15000 + increaseAmount, 18000)
  const amountOtherYears = Math.min(12000 + increaseAmount, 15000)
  return (firstEmployeeDeclaration ? amountFirstYear : amountOtherYears) / 2
}

export function computeTaxesCredit({
  grossTotal,
  socialContributionsEmployer,
  employerContributionsReduction,
  socialContributionsCredit,
  cmgEligibleAmount,
  otherAid,
  creditTaxCeil,
}) {
  const taxMax = Math.max(
    ((grossTotal +
      socialContributionsEmployer -
      employerContributionsReduction -
      socialContributionsCredit -
      cmgEligibleAmount -
      otherAid) *
      12) /
      2,
    0,
  )
  return Math.min(taxMax, creditTaxCeil)
}

export function computeAmountLeftPayableBeforeTaxes({
  totalAmount,
  socialContributionsCredit,
  employerContributionsReduction,
  cmgEligibleAmount,
  otherAid,
}) {
  return Math.max(
    totalAmount -
      socialContributionsCredit -
      employerContributionsReduction -
      cmgEligibleAmount -
      otherAid,
    0,
  )
}

export function computeAmountLeftPayable({ amountLeftPayableBeforeTaxes, taxesCredit }) {
  return Math.max(amountLeftPayableBeforeTaxes - taxesCredit / 12, 0)
}

export function computeTotalAmount({ grossTotal, socialContributionsEmployer }) {
  return grossTotal + socialContributionsEmployer
}
